import React, { useCallback, useEffect, useState } from "react";
import { IconColor } from "../icon";

interface UseIconColorStateArgs {
  name: string;
  colors?: string | IconColor[];
  color?: string;
  hover?: string | IconColor[];
  defaultColors: string[];
  onMouseEnter?: React.MouseEventHandler<SVGSVGElement>;
  onMouseLeave?: React.MouseEventHandler<SVGSVGElement>;
}

/**
 * 图标颜色状态机。逻辑与生成器模板逐字对齐，仅把图标名与默认色参数化。
 * 注意：defaultColors 是模块级共享数组，任何路径都不得原地修改它。
 */
export function useIconColorState({
  name,
  colors,
  color,
  hover,
  defaultColors,
  onMouseEnter,
  onMouseLeave,
}: UseIconColorStateArgs): {
  colorList: string[];
  handleMouseEnter: React.MouseEventHandler<SVGSVGElement>;
  handleMouseLeave: React.MouseEventHandler<SVGSVGElement>;
} {
  // 默认的color 列表
  const [colorList, setColorList] = useState(defaultColors);
  const [colorsProps, setColorsProps] = useState(defaultColors);
  const [defaultColor] = useState(defaultColors);

  useEffect(() => {
    if (typeof colors === "string" && name.indexOf("-colored") === -1) {
      const col = JSON.parse(JSON.stringify(defaultColors));
      const find = col.find((item) => item === "currentColor");
      let _col = col;
      if (find) {
        _col = col.map((item) => {
          if (item === "currentColor") {
            item = colors;
          }
          return item;
        });
      } else {
        _col[0] = colors;
      }
      setColorList(_col);
      setColorsProps(_col);
    }

    // 如果colors 是数组
    if (colors instanceof Array && name.indexOf("-colored") === -1) {
      // 获取数组中的对象
      const colorList1 = JSON.parse(JSON.stringify(defaultColors));
      colors.forEach((col) => {
        // 获取到修改的元素
        const findColorIndex = colorList1.findIndex(
          (v) => v === col.targetColor
        );
        if (findColorIndex >= 0) {
          colorList1[findColorIndex] = col.reviseColor;
        }
      });
      setColorList([...colorList1]);
      setColorsProps([...colorList1]);
    }

    if (
      color &&
      typeof color === "string" &&
      name.indexOf("-colored") === -1
    ) {
      const col = JSON.parse(JSON.stringify(defaultColors));
      const find = col.find((item) => item === "currentColor");
      let _col = col;
      if (find) {
        _col = col.map((item) => {
          if (item === "currentColor") {
            item = color;
          }
          return item;
        });
      } else {
        _col[0] = color;
      }
      setColorList(_col);
      setColorsProps(_col);
    }
  }, [JSON.stringify(colors), color, defaultColor]);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      onMouseEnter && onMouseEnter(event);
      if (!hover || hover.length === 0) return;
      if (colors instanceof Array && name.indexOf("-colored") === -1) {
        if (hover instanceof Array && name.indexOf("-colored") === -1) {
          // 获取数组中的对象
          const colorList1 = JSON.parse(JSON.stringify(colorList));
          hover.forEach((col) => {
            // 获取到修改的元素
            const findColorIndex = colorList1.findIndex(
              (v) => v === col.targetColor
            );
            if (findColorIndex >= 0) {
              colorList1[findColorIndex] = col.reviseColor;
            }
          });
          setColorList([...colorList1]);
        }
      }

      if (typeof colors === "string" && name.indexOf("-colored") === -1) {
        if (typeof hover === "string") {
          const col = JSON.parse(JSON.stringify(colorList));
          const find = col.find((item) => item === colors);
          let _col = col;
          if (find) {
            _col = col.map((item) => {
              if (item === colors) {
                item = hover;
              }
              return item;
            });
          } else {
            _col[0] = hover;
          }
          setColorList(_col);
        }
      }

      if (
        color &&
        typeof color === "string" &&
        name.indexOf("-colored") === -1
      ) {
        if (typeof hover === "string") {
          const col = JSON.parse(JSON.stringify(colorList));
          const find = col.find((item) => item === color);
          let _col = col;
          if (find) {
            _col = col.map((item) => {
              if (item === color) {
                item = hover;
              }
              return item;
            });
          } else {
            _col[0] = hover;
          }
          setColorList(_col);
        }
      }

      if (
        !color &&
        colors.length === 0 &&
        typeof hover === "string" &&
        name.indexOf("-colored") === -1
      ) {
        const col = JSON.parse(JSON.stringify(colorList));
        const find = col.find((item) => item === "currentColor");
        let _col = col;
        if (find) {
          _col = col.map((item) => {
            if (item === "currentColor") {
              item = hover;
            }
            return item;
          });
        } else {
          _col[0] = hover;
        }
        setColorList(_col);
      }

      if (
        !color &&
        colors.length === 0 &&
        hover instanceof Array &&
        name.indexOf("-colored") === -1
      ) {
        // 获取数组中的对象
        const colorList1 = JSON.parse(JSON.stringify(colorList));
        hover.forEach((col) => {
          // 获取到修改的元素
          const findColorIndex = colorList1.findIndex(
            (v) => v === col.targetColor
          );
          if (findColorIndex >= 0) {
            colorList1[findColorIndex] = col.reviseColor;
          }
        });
        setColorList([...colorList1]);
      }
    },
    [colorList, defaultColor, colors, color, hover]
  );

  const handleMouseLeave = useCallback(
    (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      onMouseLeave && onMouseLeave(event);
      if (!hover || hover.length === 0) return;
      let _defaultColor = JSON.parse(JSON.stringify(defaultColor));
      if (colorsProps) {
        _defaultColor = JSON.parse(JSON.stringify(colorsProps));
      }
      setColorList(_defaultColor);
    },
    [colorsProps, defaultColor, hover]
  );

  return {
    colorList,
    handleMouseEnter,
    handleMouseLeave,
  };
}
